import React from 'react';
import { Player, Tile } from '../types/game.ts';

interface PlayerListProps {
    players: Player[];
    currentPlayerId?: string;
    isHost?: boolean;
    targetWord?: string;
    isStreamerMode?: boolean;
    showPlayerGuesses?: boolean;
}

const PlayerList: React.FC<PlayerListProps> = ({ players, currentPlayerId, isHost = false, targetWord = '', isStreamerMode = false, showPlayerGuesses = false }) => {
    // Güvenlik kontrolü
    if (!players || !Array.isArray(players)) {
        return (
            <div className="bg-gray-800 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-4">Oyuncular (0)</h3>
                <div className="text-gray-400">Oyuncu yükleniyor...</div>
            </div>
        );
    }

    const getPlayerStatus = (player: Player) => {
        if (player.isHost) return '👑 Host';
        if (player.hasWon) return `🏆 ${player.guesses?.length || 0} tahminle kazandı`;
        if (player.isGameComplete && !player.hasWon) return '❌ Başarısız';
        if (player.guesses && player.guesses.length > 0) return `📝 ${player.guesses.length} tahmin`;
        return '⏳ Bekliyor';
    };

    const getPlayerStatusColor = (player: Player) => {
        if (player.isHost) return 'text-yellow-400';
        if (player.hasWon) return 'text-wordle-green';
        if (player.isGameComplete && !player.hasWon) return 'text-red-400';
        if (player.guesses && player.guesses.length > 0) return 'text-wordle-yellow';
        return 'text-gray-400';
    };

    const getTileColor = (tile: Tile) => {
        switch (tile.state) {
            case 'correct': return 'bg-wordle-green';
            case 'present': return 'bg-wordle-yellow';
            case 'absent': return 'bg-gray-600';
            default: return 'bg-gray-700';
        }
    };

    const renderPlayerGuesses = (player: Player) => {
        if (!isHost || !player.guesses || player.guesses.length === 0) return null;

        const shouldShowGuesses = !isStreamerMode || showPlayerGuesses;

        return (
            <div className="mt-3">
                <div className="text-xs text-gray-400 mb-2">
                    Tahminler ({player.guesses.length}):
                </div>
                <div className={`space-y-1 ${!shouldShowGuesses ? 'streamer-blur' : ''}`}>
                    {player.guesses.map((guess, index) => (
                        <div key={index} className="flex gap-1">
                            {guess.tiles.map((tile, tileIndex) => (
                                <div
                                    key={tileIndex}
                                    className={`w-5 h-5 flex items-center justify-center text-xs font-bold text-white rounded ${getTileColor(tile)}`}
                                >
                                    {tile.letter}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Oyuncuları sırala: önce kazananlar (tahmin sayısına göre), sonra başarısız olanlar
    const sortedPlayers = [...players].sort((a, b) => {
        // Host her zaman en üstte
        if (a.isHost) return -1;
        if (b.isHost) return 1;

        // Kazananlar önce
        if (a.hasWon && !b.hasWon) return -1;
        if (!a.hasWon && b.hasWon) return 1;

        // İkisi de kazandıysa tahmin sayısına göre sırala
        if (a.hasWon && b.hasWon) {
            return (a.guesses?.length || 0) - (b.guesses?.length || 0);
        }

        // İkisi de kazanmadıysa tahmin sayısına göre sırala (az tahmin önce)
        if (!a.hasWon && !b.hasWon) {
            return (a.guesses?.length || 0) - (b.guesses?.length || 0);
        }

        return 0;
    });

    return (
        <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">
                {isHost ? 'Oyuncular ve Tahminleri' : 'Oyuncular'} ({players.length})
            </h3>
            <div className="space-y-2">
                {sortedPlayers.map((player) => (
                    <div
                        key={player.id}
                        className={`p-3 rounded-lg ${player.id === currentPlayerId
                            ? 'bg-wordle-green bg-opacity-20 border border-wordle-green'
                            : 'bg-gray-700'
                            }`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-wordle-green rounded-full flex items-center justify-center text-white font-bold text-sm">
                                    {player.name.charAt(0).toUpperCase()}
                                </div>
                                <span className="text-white font-medium">{player.name}</span>
                            </div>
                            <span className={`text-sm font-medium ${getPlayerStatusColor(player)}`}>
                                {getPlayerStatus(player)}
                            </span>
                        </div>
                        {renderPlayerGuesses(player)}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PlayerList;
